import { useRequiredContext } from "@redotech/react-util/context";
import { useAsyncThrottle } from "@redotech/react-util/debounce";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { checkHasAnyOpenConversations } from "@redotech/redo-merchant-app-common/client/team";
import { MerchantAppTopic } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server";
import { MerchantAppEventServerContext } from "@redotech/redo-merchant-app-common/events/merchant-app-event-server-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { useStreamingEnabled } from "@redotech/redo-merchant-fulfillment-app/common/streaming-enabled-context";
import { maybeRunConversationsRefresh } from "@redotech/redo-merchant-support-app/handle-notice-event";
import { SupportPermissions } from "@redotech/redo-model/permissions";
import { FrontendTeamNoticeEvent } from "@redotech/redo-model/team-notifications/team-notifications";
import { sinkPromise } from "@redotech/util/promise";
import { useContext, useEffect, useState } from "react";

export function useHasAnyOpenConversations(): boolean {
  const client = useRequiredContext(RedoMerchantClientContext);
  const eventServer = useRequiredContext(MerchantAppEventServerContext);
  const team = useContext(TeamContext);
  const streamingEnabled = useStreamingEnabled();
  const hasSupportPermission = useHasPermission(
    SupportPermissions.VIEW_SUPPORT,
  );

  const [hasAny, setHasAny] = useState(false);

  const supportEnabled = team?.settings.support?.enabled;
  const shouldCheck =
    streamingEnabled && supportEnabled && hasSupportPermission;

  const fetchHasAny = useHandler(async () => {
    if (!shouldCheck) {
      return;
    }
    try {
      const result = await checkHasAnyOpenConversations(client, {});
      setHasAny(result.hasAny);
    } catch (err) {
      console.warn("Failed to fetch open conversations status", err);
    }
  });

  const throttledFetch = useAsyncThrottle(
    fetchHasAny,
    Temporal.Duration.from({ seconds: 30 }),
  );

  const fetchIgnoreResult = useHandler(() => {
    sinkPromise(throttledFetch());
  });

  useEffect(() => {
    if (shouldCheck) {
      sinkPromise(fetchHasAny());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCheck]);

  useEffect(() => {
    if (!shouldCheck) {
      return;
    }
    const unsub = eventServer.subscribe({
      topic: MerchantAppTopic.TEAM,
      callback: async (message: FrontendTeamNoticeEvent) => {
        maybeRunConversationsRefresh(message, fetchIgnoreResult);
      },
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCheck]);

  return hasAny;
}
